import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import {
  AlertCircle,
  ArrowLeft,
  CheckCircle,
  ChevronRight,
  Loader2,
  RefreshCw,
  ShieldCheck,
  XCircle,
} from 'lucide-react';
import type {
  InterventionPriorite,
  InterventionStatut,
  InterventionStatutValidation,
  InterventionTypeValidation,
} from '@mygmao/shared';
import { api, extractErrorMessage } from '../lib/api';
import { usePermissions } from '../hooks/usePermissions';
import { InterventionDetailDrawer } from '../components/interventions/InterventionDetailDrawer';
import { RespondValidationModal } from '../components/interventions/ValidationsPanel';
import {
  PrioriteBadge,
  StatutInterventionBadge,
  typeValidationLabel,
} from '../components/interventions/badges';

/**
 * Page `/mes-validations` (US-INT-04).
 *
 * File de validations en attente où je suis valideur, triée par priorité de
 * la demande.
 */

interface ValidationItem {
  id: string;
  intervention_id: string;
  type_validation: InterventionTypeValidation;
  statut_validation: InterventionStatutValidation;
  created_at: string;
  intervention: {
    id: string;
    numero_demande: string;
    titre: string;
    priorite: InterventionPriorite;
    statut: InterventionStatut;
    date_demande: string;
  };
}

export function MesValidationsPage() {
  const { can } = usePermissions();
  const canRead = can('interventions', 'read');

  const [items, setItems] = useState<ValidationItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [respondTo, setRespondTo] = useState<{
    row: ValidationItem;
    approve: boolean;
  } | null>(null);
  const [detailId, setDetailId] = useState<string | null>(null);

  async function refresh() {
    setLoading(true);
    setErrorMessage(null);
    try {
      const res = await api.get<{ data: ValidationItem[] }>(
        '/interventions/validations/mes-validations',
      );
      setItems(res.data.data);
    } catch (err) {
      setErrorMessage(extractErrorMessage(err));
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    if (canRead) refresh();
  }, [canRead]);

  if (!canRead) {
    return (
      <div className="max-w-7xl mx-auto p-6">
        <p className="text-sm text-gray-500">Vous n'avez pas accès au module Interventions.</p>
      </div>
    );
  }

  return (
    <div className="max-w-7xl mx-auto space-y-4">
      <div className="flex items-start justify-between flex-wrap gap-3">
        <div>
          <Link
            to="/interventions"
            className="inline-flex items-center text-xs text-gray-500 hover:text-gray-800 mb-1"
          >
            <ArrowLeft className="w-3 h-3 mr-1" />
            Retour aux interventions
          </Link>
          <h1 className="text-2xl font-bold text-gray-900 flex items-center">
            <ShieldCheck className="w-7 h-7 mr-2 text-primary-600" />
            Mes validations
          </h1>
          <p className="text-sm text-gray-500 mt-1">
            {items.length} validation{items.length > 1 ? 's' : ''} en attente
          </p>
        </div>
        <button
          onClick={refresh}
          className="inline-flex items-center px-3 py-2 border border-gray-300 rounded-lg text-sm text-gray-700 bg-white hover:bg-gray-50"
        >
          <RefreshCw className="w-4 h-4 mr-1.5" />
          Rafraîchir
        </button>
      </div>

      {errorMessage && (
        <div className="rounded-lg border border-red-200 bg-red-50 p-3 flex items-start space-x-2">
          <AlertCircle className="w-4 h-4 text-red-600 flex-shrink-0 mt-0.5" />
          <p className="text-sm text-red-700">{errorMessage}</p>
        </div>
      )}

      {loading ? (
        <div className="flex items-center justify-center p-12">
          <Loader2 className="w-8 h-8 animate-spin text-primary-500" />
        </div>
      ) : items.length === 0 ? (
        <div className="rounded-xl border border-green-200 bg-green-50 p-8 text-center">
          <CheckCircle className="w-10 h-10 text-green-600 mx-auto mb-2" />
          <p className="text-sm font-medium text-green-900">Aucune validation en attente.</p>
          <p className="text-xs text-green-700 mt-1">
            Vous êtes à jour sur les validations qui vous sont assignées.
          </p>
        </div>
      ) : (
        <ul className="space-y-2">
          {items.map((v) => (
            <li
              key={v.id}
              className="bg-white rounded-xl border border-gray-200 p-4 hover:shadow transition-shadow"
            >
              <div className="flex items-start justify-between flex-wrap gap-3">
                <div className="min-w-0 flex-1">
                  <div className="flex items-center flex-wrap gap-2 mb-1">
                    <button
                      onClick={() => setDetailId(v.intervention.id)}
                      className="text-xs font-semibold text-primary-600 uppercase tracking-wide hover:underline"
                    >
                      {v.intervention.numero_demande}
                    </button>
                    <PrioriteBadge priorite={v.intervention.priorite} compact />
                    <StatutInterventionBadge statut={v.intervention.statut} />
                  </div>
                  <p className="text-sm font-semibold text-gray-900 mb-1">{v.intervention.titre}</p>
                  <p className="text-xs text-gray-600">
                    Type de validation :{' '}
                    <span className="font-medium">{typeValidationLabel(v.type_validation)}</span>
                  </p>
                  <p className="text-xs text-gray-400 mt-1">
                    Demande créée le {new Date(v.intervention.date_demande).toLocaleString()} ·
                    Validation requise depuis {new Date(v.created_at).toLocaleString()}
                  </p>
                </div>
                <div className="flex items-center space-x-2 flex-shrink-0">
                  <button
                    onClick={() => setRespondTo({ row: v, approve: true })}
                    className="inline-flex items-center px-3 py-1.5 bg-green-500 hover:bg-green-600 text-white rounded-lg text-xs font-medium"
                  >
                    <CheckCircle className="w-3.5 h-3.5 mr-1" />
                    Approuver
                  </button>
                  <button
                    onClick={() => setRespondTo({ row: v, approve: false })}
                    className="inline-flex items-center px-3 py-1.5 bg-red-500 hover:bg-red-600 text-white rounded-lg text-xs font-medium"
                  >
                    <XCircle className="w-3.5 h-3.5 mr-1" />
                    Rejeter
                  </button>
                  <button
                    onClick={() => setDetailId(v.intervention.id)}
                    className="p-1.5 rounded-lg hover:bg-gray-100 text-gray-500"
                    title="Voir détail"
                  >
                    <ChevronRight className="w-4 h-4" />
                  </button>
                </div>
              </div>
            </li>
          ))}
        </ul>
      )}

      {respondTo && (
        <RespondValidationModal
          row={respondTo.row}
          approve={respondTo.approve}
          onClose={() => setRespondTo(null)}
          onSaved={() => {
            setRespondTo(null);
            refresh();
          }}
        />
      )}

      {detailId && (
        <InterventionDetailDrawer
          interventionId={detailId}
          onClose={() => setDetailId(null)}
          onRefresh={refresh}
        />
      )}
    </div>
  );
}
